import React, { forwardRef, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Paragraph } from "@purpur/paragraph";
import * as RadixRadioGroup from "@radix-ui/react-radio-group";

import { cx } from "./classnames";
import {
  isRadioCardItemImageProps,
  RadioCardImage,
  type RadioCardImageProps,
} from "./radio-card-item-image";

const rootClassName = "purpur-radio-card-group__item";

type CustomElement = ((props: { disabled?: boolean }) => ReactNode) | ReactNode;
type NonNullCustomElement =
  | ((props: { disabled?: boolean }) => NonNullable<ReactNode>)
  | NonNullable<ReactNode>;

export type RadioCardItemProps = Omit<BaseProps, "id" | "title"> & {
  /**
   * `string | ReactNode | (props: { disabled?: boolean }) => ReactNode`
   */
  body?: string | CustomElement;
  disabled?: boolean;
  id: string;
  /**
   * The radio image.
   *
      union = {
        "data-testid"?: string | undefined;
        altText: string;
        loading?: "lazy" | "eager" | undefined;
        noPlaceholder?: boolean | undefined;
        src: string;
      } |
      ((props: {
        disabled?: boolean;
      }) => ReactNode) | React.ReactNode
   * */
  image?: RadioCardImageProps | CustomElement;
  value: string;
  /**
   * `union = string | Omit<ReactNode, "undefined" | "null">`
   */
  title: string | NonNullCustomElement;
};

const renderCustomElement = (
  element: CustomElement | NonNullCustomElement,
  { disabled }: Pick<RadioCardItemProps, "disabled">
) => (typeof element === "function" ? element({ disabled }) : element);

export const RadioCardItem = forwardRef<HTMLDivElement, RadioCardItemProps>(
  ({ title, ...propsx }, ref) => {
    const { ["data-testid"]: dataTestid, body, children, disabled, id, image, value } = propsx;

    const props = { title, ...propsx };

    return (
      <div className={cx(`${rootClassName}-container`)} ref={ref}>
        <RadixRadioGroup.Item
          className={cx(rootClassName)}
          data-testid={dataTestid}
          disabled={disabled}
          id={id}
          value={value}
        >
          {image && (
            <span className={cx(`${rootClassName}-image-container`)}>
              {isRadioCardItemImageProps(image) ? (
                <RadioCardImage {...image} data-testid={dataTestid && `${dataTestid}-image`} />
              ) : (
                renderCustomElement(image, props)
              )}
            </span>
          )}
          <span className={cx(`${rootClassName}-content`)}>
            <span className={cx(`${rootClassName}-top-container`)}>
              {typeof title === "string" ? (
                <Paragraph
                  className={cx(`${rootClassName}-title`)}
                  data-testid={dataTestid && `${dataTestid}-title`}
                  variant="paragraph-100"
                  disabled={disabled}
                >
                  {title}
                </Paragraph>
              ) : (
                renderCustomElement(title, props)
              )}
              <span className={cx(`${rootClassName}-radio`)}>
                <RadixRadioGroup.Indicator className={cx(`${rootClassName}-indicator`)} />
              </span>
            </span>
            {body && (
              <span className={cx(`${rootClassName}-bottom-container`)}>
                {typeof body === "string" ? (
                  <Paragraph
                    data-testid={dataTestid && `${dataTestid}-body`}
                    variant="paragraph-100"
                    disabled={disabled}
                  >
                    {body}
                  </Paragraph>
                ) : (
                  renderCustomElement(body, props)
                )}
              </span>
            )}
          </span>
        </RadixRadioGroup.Item>
        {children}
      </div>
    );
  }
);

RadioCardItem.displayName = "RadioCardItem";
